import { FLEXIBLE_SIDES_REGION_IDS } from 'common/Constants';
import { supportsMealPlans } from 'common/MealPlanUtils';
import { SessionStorageKeys, StorageKeys } from 'common/storage/constants';
import { isNil, omitBy } from 'lodash';
import QueryString, { StringifiableRecord } from 'query-string';
import { useContext } from 'react';
import { useHistory } from 'react-router';
import { MealPlanFormat } from 'src/gqlReactTypings.generated.d';
import { useFeatureFlag } from 'src/shared/design-system/FeatureFlag';
import { useCurrentUser } from 'src/shared/hooks/useCurrentUserHook';
import { Routes } from 'src/shared/Routes';
import { useLocalStorage, useSessionStorage } from 'src/shared/storage/Storage';
import { getFullRelativeUrl, locationInRoutes } from 'src/shared/utils/RouteUtilities';
import { useCachedZipCode } from 'src/user-preferences/useCachedZipCode';
import { useMealPlanIntakeStoreSelectors } from 'src/ztore/meal-plan-intake-store';
import { match, P } from 'ts-pattern';
import { StringParam, useQueryParams, withDefault } from 'use-query-params';
import { MealPlanEntryPoints } from '../consts';
import { STEP_1, STEP_HOUSEHOLD } from '../meal-plan-intake/preferences/consts';
import { MealPlanSubscriptionContext } from '../MealPlanSubscriptionsContext';

type GetMealPlanUrlParams = {
  format?: MealPlanFormat;
  skipSplashScreen?: boolean;
  entryPoint?: MealPlanEntryPoints;
  step?: string;
};

interface MealPlanSurfacingLogicReturn {
  isInMealPlanRegion: boolean;
  isInFlexibleSidesRegion: boolean;
  mealPlanHasBeenRedirected: boolean;
  navigateToMealPlanPreferences: (params: GetMealPlanUrlParams) => void;
  setHasSeenMealPlanModal: () => void;
  setMealPlanHasBeenRedirected: () => void;
  shefLogoDisabled: boolean;
  shouldShowMealPlanBanner: boolean;
  shouldShowMealPlanButton: boolean;
  shouldShowMealPlanModal: boolean;
  getMealPlanUrl: (params: GetMealPlanUrlParams) => string;
  visitedMealPlans: boolean;
  setVisitedMealPlans: (value: boolean) => void;
  intakeCompletedAtDate?: string;
}

export const useMealPlanSurfacingLogic = (): MealPlanSurfacingLogicReturn => {
  const [currentUser, currentUserLoading] = useCurrentUser();
  const { userHasActiveMealPlan } = useContext(MealPlanSubscriptionContext);
  const history = useHistory();
  const { location } = history;
  const userIsLoggedIn = !isNil(currentUser) && !currentUserLoading;
  const [, , zipCodeMetaData] = useCachedZipCode();
  const regionId = zipCodeMetaData?.cachedZipCodeData?.zipCode?.region?.id;
  const isMealPlanEnabledRegion = supportsMealPlans(
    regionId,
    zipCodeMetaData?.cachedZipCodeData?.zipCode?.deliveryHubId
  );
  const isInFlexibleSidesRegion = FLEXIBLE_SIDES_REGION_IDS.includes(regionId ?? 0) && isMealPlanEnabledRegion;
  const { getFormData } = useMealPlanIntakeStoreSelectors();
  const { mealPlanType, intakeCompletedAtDate } = getFormData();
  const userHasCompletedIntakeForm = !isNil(intakeCompletedAtDate);
  const returnUxImprovementsEnabled = useFeatureFlag('returnUserUxImprovementsEnabled');

  const isInIntakeFlow = locationInRoutes(location, [
    Routes.CONSUMER_MEAL_PLANS_PREFERENCES,
    Routes.CONSUMER_MEAL_PLANS_CHECKOUT,
  ]);

  const [{ entryPoint }] = useQueryParams({
    entryPoint: withDefault(StringParam, undefined),
  });

  const [visitedMealPlans, setVisitedMealPlans] = useLocalStorage<boolean>(StorageKeys.VISITED_MEAL_PLANS, false);
  const [hasSeenMealPlanModalLifetime, setHasSeenMealPlanModalLifetime] = useLocalStorage<boolean>(
    StorageKeys.USER_HAS_SEEN_EXPLORE_MEAL_PLAN_PROPOSITION_MODAL,
    false
  );
  const [hasSeenMealPlanModalSession, setHasSeenMealPlanModalSession] = useSessionStorage<boolean>(
    SessionStorageKeys.USER_HAS_SEEN_EXPLORE_MEAL_PLAN_PROPOSITION_MODAL,
    false
  );
  const [mealPlanHasBeenRedirected, setMealPlanHasBeenRedirectedSession] = useSessionStorage<boolean>(
    SessionStorageKeys.MEAL_PLAN_HAS_BEEN_REDIRECTED,
    false
  );

  const setHasSeenMealPlanModal = () => {
    setHasSeenMealPlanModalLifetime(true);
    setHasSeenMealPlanModalSession(true);
  };

  const setMealPlanHasBeenRedirected = () => {
    setMealPlanHasBeenRedirectedSession(true);
  };

  const shefLogoDisabled = match({ isInIntakeFlow, entryPoint })
    .with({ isInIntakeFlow: true, entryPoint: MealPlanEntryPoints.MPLandingPage }, () => true)
    .otherwise(() => false);

  const formatUrl = (url: string, query: StringifiableRecord) =>
    QueryString.stringifyUrl({
      url,
      query,
    });

  const getMealPlanUrl = (props: GetMealPlanUrlParams): string => {
    const { format, skipSplashScreen, step } = props;
    if (!isMealPlanEnabledRegion) return '';
    if (userHasActiveMealPlan) return Routes.CONSUMER_MEAL_PLANS_UPCOMING_ORDER;

    if (userHasCompletedIntakeForm && returnUxImprovementsEnabled) {
      return formatUrl(Routes.CONSUMER_MEAL_PLANS_CHECKOUT, omitBy({ entryPoint }, isNil));
    }

    const formatToUse = match({ mealPlanType, format, isInFlexibleSidesRegion })
      .with({ format: P.when((val) => !isNil(val)) }, () => format)
      .with({ mealPlanType: P.when((mpt) => !isNil(mpt?.format)) }, ({ mealPlanType: mpt }) => mpt.format)
      .with({ isInFlexibleSidesRegion: true }, () => MealPlanFormat.FlexibleSides)
      .otherwise(() => MealPlanFormat.MainsPlusSides);

    const intakeStep = match({ step, skipSplashScreen })
      .with({ step: P.when((val) => !isNil(val)) }, () => step)
      .with({ skipSplashScreen: true }, () => STEP_HOUSEHOLD)
      .otherwise(() => STEP_1);

    const entryPointToUse = props.entryPoint ?? entryPoint;

    const query = omitBy({ entryPoint: entryPointToUse, format: formatToUse, step: intakeStep }, isNil);
    return formatUrl(Routes.CONSUMER_MEAL_PLANS_PREFERENCES, query);
  };

  const navigateToMealPlanPreferences = (props: GetMealPlanUrlParams) => {
    const from = getFullRelativeUrl(history.location);
    const url = getMealPlanUrl(props);

    history.push(url, { from });
  };

  /**
   * Determine if the user should see the persistent meal plan banner on Explore
   */
  const shouldShowMealPlanBanner: boolean = match({
    visitedMealPlans,
    isInMealPlanRegion: isMealPlanEnabledRegion,
  })
    .with({ isInMealPlanRegion: false }, () => false)
    .otherwise(() => true);

  /**
   * Determine if the user should see the meal plan modal on Explore
   */
  const shouldShowMealPlanModal: boolean = match({
    userIsLoggedIn,
    visitedMealPlans,
    hasSeenMealPlanModalLifetime,
    hasSeenMealPlanModalSession,
    isInMealPlanRegion: isMealPlanEnabledRegion,
    userHasActiveMealPlan,
  })
    .with({ isInMealPlanRegion: false }, () => false)
    .with({ userHasActiveMealPlan: true }, () => false)
    .with({ userIsLoggedIn: true, visitedMealPlans: true, hasSeenMealPlanModalLifetime: true }, () => false)
    .with({ userIsLoggedIn: true, visitedMealPlans: false, hasSeenMealPlanModalSession: true }, () => false)
    .with({ userIsLoggedIn: false, visitedMealPlans: true, hasSeenMealPlanModalLifetime: true }, () => false)
    .with({ userIsLoggedIn: false, visitedMealPlans: false, hasSeenMealPlanModalSession: true }, () => false)
    .otherwise(() => true);

  /**
   * Determine whether the Meal Plan button should be shown in the hamburger menu
   */
  const shouldShowMealPlanButton = match({
    userHasActiveMealPlan,
    visitedMealPlans,
    regionId,
    isInMealPlanRegion: isMealPlanEnabledRegion,
  })
    .with({ userHasActiveMealPlan: true }, () => true)
    .with(
      P.when((val) => !val.isInMealPlanRegion && val.regionId !== undefined),
      () => false
    )
    .otherwise(() => true);

  return {
    isInMealPlanRegion: isMealPlanEnabledRegion,
    isInFlexibleSidesRegion,
    mealPlanHasBeenRedirected,

    // handle navigating user to meal plan urls
    getMealPlanUrl,
    navigateToMealPlanPreferences,

    setHasSeenMealPlanModal,
    setMealPlanHasBeenRedirected,
    shefLogoDisabled,
    shouldShowMealPlanBanner,
    shouldShowMealPlanButton,
    shouldShowMealPlanModal,
    visitedMealPlans,
    setVisitedMealPlans,
    intakeCompletedAtDate,
  };
};
